import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';

import { faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, spacing, typography }) => ({
  display: 'flex',
  '.DCI-button': { marginLeft: spacing(0.5) },
  '.DCI-input_active': {
    fontWeight: typography.fontWeightExtraBold,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
  '.DCI-inputRoot_active': {
    color: palette.primary.main,
    [`&:hover .${outlinedInputClasses.notchedOutline}`]: { borderColor: palette.primary.main },
  },
  '.DCI-inputRoot_error': {
    color: palette.secondary.main,
    [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}, &:hover .${outlinedInputClasses.notchedOutline}`]:
      {
        borderColor: palette.secondary.main,
      },
  },
});

export default function DiscountCodeInput(props) {
  const {
    ButtonProps,
    className,
    defaultQuery,
    disabled,
    onVerified,
    onVerify,
    TextFieldProps,
    verifyDefaultQuery,
    verifyStatus,
  } = props;

  const [isValidCode, setIsValidCode] = useState(null);
  useEffect(() => {
    if (verifyStatus && !verifyStatus?.fetching) setIsValidCode(!!verifyStatus?.success);
  }, [verifyStatus]);

  const [query, setQuery] = useState(defaultQuery || '');
  useEffect(() => {
    if (defaultQuery) setQuery(query => query || defaultQuery);
  }, [defaultQuery]);
  const updateQuery = useCallback(
    e => {
      if (isValidCode) onVerified(null);
      setIsValidCode(null);
      setQuery(e?.target?.value || '');
    },
    [isValidCode, onVerified],
  );

  const queryEqualsDefault = defaultQuery === query;
  useEffect(() => {
    if (!(verifyDefaultQuery && defaultQuery && queryEqualsDefault)) return;
    onVerify(defaultQuery);
  }, [verifyDefaultQuery, onVerify, defaultQuery, queryEqualsDefault]);

  return (
    <form
      className={className}
      css={rootCss}
      onSubmit={e => {
        e.preventDefault();
        onVerify(query);
      }}
    >
      <TextField
        disabled={disabled || !!verifyStatus?.fetching}
        fullWidth
        margin="none"
        onChange={updateQuery}
        placeholder="Add Promo Code"
        size="small"
        value={query}
        {...TextFieldProps}
        slotProps={{
          input: {
            classes: {
              input: classNames({ 'DCI-input_active': query }),
              root: classNames({
                'DCI-inputRoot_active': query,
                'DCI-inputRoot_error': isValidCode === false,
              }),
            },
            endAdornment: isValidCode !== null && (
              <InputAdornment position="end">
                {isValidCode ?
                  <FontAwesomeIcon icon={faCheck} />
                : <IconButton color="secondary" onClick={() => updateQuery()} size="small">
                    <FontAwesomeIcon icon={faTimes} />
                  </IconButton>
                }
              </InputAdornment>
            ),
          },
          ...TextFieldProps?.slotProps,
        }}
      />
      {isValidCode === null && (
        <Button
          className="DCI-button"
          disabled={disabled || !!verifyStatus?.fetching}
          size="medium"
          type="submit"
          variant="contained"
          {...ButtonProps}
        >
          Apply
        </Button>
      )}
    </form>
  );
}
